import { ToggleAIConversationDialog } from '@/components/conversations/conversation-chat/ToggleAIConversationDialog';
import { TextToAudioModal } from '@/components/conversations/conversation-chat/TextToAudioModal';
import { Button, ImageIcon, Mic, Paperclip, Smile, Textarea, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, X } from '@/components/ui';
import { Globe, Loader2, SpeechToAudio } from '@/components/ui/icons/Icons';
import { useI18n } from '@/context/I18nProvider';
import { languageDisplayName as languageName } from '@/hooks/useConversationTranslation';
import { useManualReplyPreference } from '@/hooks/useManualReplyPreference';
import { cn } from '@/lib/utils';
import { ApiClient } from '@/services/api-client';
import dynamic from 'next/dynamic';
import socketClient from '@/services/socket-client';
const EmojiPicker = dynamic(() => import('@/components/ui/EmojiPicker').then(mod => mod.EmojiPicker), {
  ssr: false
});
import { ChangeEvent, KeyboardEvent, useRef, useState } from 'react';
import { toast } from 'sonner';
type ChatMessage = {
  text: string;
  attachments?: File[];
  clientMessageId: string;
};
type ChatInputProps = {
  conversationId: string;
  placeholder?: string;
  onSend?: (message: ChatMessage) => void;
  orgId: string;
  leadId: string;
  isAgentActive: boolean;
  /** Conversation override of the workspace manual-reply setting; null follows it. */
  keepAgentOnManualReply?: boolean | null;
  isInstagramConversation?: boolean;
  isChatInputDisabled?: boolean;
  /**
   * Set when the conversation is translated: replies are written in the user's
   * language, translated into the lead's and reviewed before being sent.
   */
  translateReplies?: {
    targetLanguageName: string;
    onSent: (translated: string, original: string) => void;
  };
};
type TranslationPreview = {
  original: string;
  translated: string;
};
const ChatInput = ({
  conversationId,
  placeholder,
  onSend,
  orgId,
  leadId,
  isAgentActive,
  keepAgentOnManualReply,
  isInstagramConversation = false,
  isChatInputDisabled = false,
  translateReplies
}: ChatInputProps) => {
  const {
    t,
    locale
  } = useI18n();
  const {
    data: orgKeepsAgentOn = false
  } = useManualReplyPreference();
  // When this conversation (or, by default, the org) keeps the agent on after a
  // manual reply, sending doesn't have to switch it off first — so no
  // confirmation dialog and no warning tooltip.
  const sendingDisablesAgent = isAgentActive && !(keepAgentOnManualReply ?? orgKeepsAgentOn);
  const resolvedPlaceholder = placeholder || t('conversations.replyPlaceholder');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [disableAgentTooltipOpen, setDisableAgentTooltipOpen] = useState(false);
  const [ttsAudioS3Key, setTtsAudioS3Key] = useState<string | null>(null);
  const [ttsAudioLabel, setTtsAudioLabel] = useState<string | null>(null);
  const [preview, setPreview] = useState<TranslationPreview | null>(null);
  const [isTranslatingReply, setIsTranslatingReply] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const dialogTriggerRef = useRef<HTMLButtonElement>(null);
  const isSendButtonDisabled = !message.trim() && attachments.length === 0 && !ttsAudioS3Key || isChatInputDisabled || isTranslatingReply;
  const handleSend = async () => {
    if (!message.trim() && attachments.length === 0 && !ttsAudioS3Key) return;
    if (isTranslatingReply) return;

    // Translated conversation: translate first, the user confirms in the preview.
    if (translateReplies && message.trim() && !preview) {
      const original = message.trim();
      setIsTranslatingReply(true);
      try {
        const {
          data
        } = await ApiClient.lead.translateOutgoing({
          leadId,
          text: original,
          sourceLanguage: locale
        });
        setPreview({
          original,
          translated: data.Translation
        });
      } catch {
        toast.error(t('conversations.translation.replyError'));
      } finally {
        setIsTranslatingReply(false);
      }
      return;
    }
    await sendText(preview ? preview.translated.trim() : message.trim());
  };
  const sendText = async (textToSend: string) => {
    if (!textToSend && attachments.length === 0 && !ttsAudioS3Key) return;
    const audioKey = ttsAudioS3Key;
    const clientMessageId = crypto.randomUUID();
    onSend?.({
      text: textToSend,
      attachments: attachments.length > 0 ? attachments : undefined,
      clientMessageId
    });
    // Reset state after sending
    setMessage('');
    setAttachments([]);
    setTtsAudioS3Key(null);
    setTtsAudioLabel(null);
    if (preview && textToSend) translateReplies?.onSent(textToSend, preview.original);
    setPreview(null);
    await socketClient.sendMessage(orgId, leadId, textToSend, audioKey || undefined, clientMessageId);
  };
  const handleAudioGenerated = (_audioUrl: string, s3Key: string, text: string) => {
    setTtsAudioS3Key(s3Key);
    setTtsAudioLabel(text || t('tts.audioAttached'));
  };
  const handleEmojiSelect = (emoji: string) => {
    setMessage(prev => prev + emoji);
  };
  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>, type: 'image' | 'file') => {
    const files = event.target.files;
    if (!files) return;
    const newFiles = Array.from(files);
    if (type === 'image') {
      const imageFiles = newFiles.filter(file => file.type.startsWith('image/'));
      setAttachments(prev_0 => [...prev_0, ...imageFiles]);
    } else {
      setAttachments(prev_1 => [...prev_1, ...newFiles]);
    }

    // Reset the input
    event.target.value = '';
  };
  const handleVoiceRecord = async () => {
    try {
      if (!isRecording) {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });
        // Instagram API accepts mp4, ogg, mp3, m4a, wav — but NOT webm.
        const mimeType = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus') ? 'audio/ogg;codecs=opus' : 'audio/webm;codecs=opus';
        const ext = mimeType.includes('mp4') ? 'm4a' : mimeType.includes('ogg') ? 'ogg' : 'webm';
        const mediaRecorder = new MediaRecorder(stream, {
          mimeType
        });
        const audioChunks: BlobPart[] = [];
        mediaRecorder.ondataavailable = event_0 => {
          audioChunks.push(event_0.data);
        };
        mediaRecorder.onstop = () => {
          const audioBlob = new Blob(audioChunks, {
            type: mimeType
          });
          const audioFile = new File([audioBlob], `voice-message.${ext}`, {
            type: mimeType
          });
          setAttachments(prev_2 => [...prev_2, audioFile]);
        };
        mediaRecorder.start();
        setIsRecording(true);

        // Stop recording after 1 minute
        setTimeout(() => {
          if (mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
            setIsRecording(false);
          }
        }, 60000);
      } else {
        setIsRecording(false);
      }
    } catch {
      toast.error(t('conversations.microphoneError'));
    }
  };
  const handleTextareaKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      if (isChatInputDisabled) return;
      // The agent dialog was already confirmed before the translation.
      if (preview) {
        handleSend();
      } else if (sendingDisablesAgent) {
        dialogTriggerRef.current?.click();
      } else {
        handleSend();
      }
    } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      setMessage(prev_3 => prev_3 + '\n');
    }
  };
  return <div className={'flex w-full flex-col gap-2.5 px-3 sm:px-6 py-2 sm:py-3 pb-1'}>
      <TooltipProvider>
        <Tooltip open={(sendingDisablesAgent || isChatInputDisabled) && disableAgentTooltipOpen}>
          <TooltipTrigger asChild>
            <div className="bg-white w-full relative flex flex-col rounded-md border border-border px-3 sm:px-4 py-2" onMouseEnter={() => setDisableAgentTooltipOpen(true)} onMouseLeave={() => {
            if (document.activeElement !== inputRef.current) {
              setDisableAgentTooltipOpen(false);
            }
          }}>
              {preview && translateReplies ? <div className="mb-2 flex flex-col gap-1.5 rounded-md border border-blue-200 bg-blue-50/60 p-2.5">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-blue-700">
                    <Globe className="size-3.5" />
                    {t('conversations.translation.previewTitle', {
                  language: translateReplies.targetLanguageName
                })}
                  </span>
                  <Textarea value={preview.translated} onChange={e_0 => setPreview({
                ...preview,
                translated: e_0.target.value
              })} onKeyDown={e_1 => {
                if (e_1.key === 'Enter' && !e_1.ctrlKey && !e_1.metaKey && !e_1.shiftKey) {
                  e_1.preventDefault();
                  handleSend();
                } else if (e_1.key === 'Escape') {
                  setPreview(null);
                }
              }} maxLength={isInstagramConversation ? 1000 : 4096} autoFocus className="min-h-14 resize-none bg-background text-[14px] leading-5 shadow-none" />
                  <span className="text-xs text-muted-foreground">
                    {t('conversations.translation.previewOriginal', {
                  text: preview.original
                })}
                  </span>
                  <div className="flex items-center justify-end gap-2">
                    <Button type="button" size="sm" variant="ghost" className="h-7 px-3 text-xs" onClick={() => setPreview(null)}>
                      {t('common.cancel')}
                    </Button>
                    <Button type="button" size="sm" variant="secondary" className="h-7 px-3 text-xs" onClick={() => handleSend()} disabled={!preview.translated.trim() || isChatInputDisabled}>
                      {t('conversations.translation.sendTranslation')}
                    </Button>
                  </div>
                </div> : null}

              <Textarea ref={inputRef} value={message} onFocus={() => setDisableAgentTooltipOpen(true)} onBlur={() => setDisableAgentTooltipOpen(false)} onChange={e_2 => {
              setMessage(e_2.target.value);
              // The preview no longer matches what the user is writing.
              setPreview(null);
              setDisableAgentTooltipOpen(true);
            }} onKeyDown={handleTextareaKeyDown} maxLength={isInstagramConversation ? 1000 : 4096} className="min-h-10 sm:min-h-14 flex-1 resize-none border-0 bg-transparent px-0 py-0 text-[14px] leading-5 placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-none shadow-none" placeholder={resolvedPlaceholder} disabled={isChatInputDisabled} />

              {(attachments.length > 0 || ttsAudioS3Key) && <div className="mt-2 flex flex-wrap gap-2">
                  {ttsAudioS3Key && <div className="flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1">
                      <SpeechToAudio className="size-3 text-primary" />
                      <span className="text-xs text-primary font-medium">{ttsAudioLabel}</span>
                      <button onClick={() => {
                  setTtsAudioS3Key(null);
                  setTtsAudioLabel(null);
                }} className="ml-1 text-primary/60 hover:text-primary">
                        <X className="size-3" />
                      </button>
                    </div>}
                  {attachments.map((file_0, index) => <div key={index} className="flex items-center gap-1 rounded-md bg-accent px-2 py-1">
                      <span className="text-xs">{file_0.name}</span>
                      <button onClick={() => setAttachments(prev_4 => prev_4.filter((_, i) => i !== index))} className="ml-1 text-muted-foreground hover:text-foreground">
                        <X className="size-3" />
                      </button>
                    </div>)}
                </div>}

              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <EmojiPicker onEmojiSelect={handleEmojiSelect} disabled={isChatInputDisabled}>
                    <button className={cn('flex size-4 items-center justify-center text-muted-foreground hover:text-foreground', isChatInputDisabled && 'cursor-not-allowed opacity-50 hover:text-none')} aria-label={t('common.addEmoji')}>
                      <Smile className="size-4" />
                    </button>
                  </EmojiPicker>
                  {!isInstagramConversation ? <>
                      <button onClick={() => imageInputRef.current?.click()} className={cn('flex size-4 items-center justify-center text-muted-foreground hover:text-foreground', isChatInputDisabled && 'cursor-not-allowed opacity-50 hover:text-none')} aria-label={t('common.addImage')} disabled={isChatInputDisabled}>
                        <ImageIcon className="size-4" />
                      </button>

                      <button onClick={() => fileInputRef.current?.click()} className={cn('flex size-4 items-center justify-center text-muted-foreground hover:text-foreground', isChatInputDisabled && 'cursor-not-allowed opacity-50 hover:text-none')} aria-label={t('common.attachFile')} disabled={isChatInputDisabled}>
                        <Paperclip className="size-4" />
                      </button>

                      <button onClick={handleVoiceRecord} className={cn('flex size-4 items-center justify-center text-muted-foreground hover:text-foreground', isRecording && 'text-destructive', isChatInputDisabled && 'cursor-not-allowed opacity-50 hover:text-none')} aria-label={t('common.voiceMessage')} disabled={isChatInputDisabled}>
                        <Mic className="size-4" />
                      </button>
                    </> : null}

                  <TextToAudioModal onAudioGenerated={handleAudioGenerated} disabled={isChatInputDisabled} initialText={message} trigger={<button className={cn('flex size-4 items-center justify-center text-muted-foreground hover:text-foreground', ttsAudioS3Key && 'text-primary', isChatInputDisabled && 'cursor-not-allowed opacity-50 hover:text-none')} aria-label={t('tts.title')}>
                        <SpeechToAudio className="size-4" />
                      </button>} />
                </div>

                {translateReplies ? <span className="ml-auto mr-3 hidden items-center gap-1 truncate text-xs text-muted-foreground sm:flex">
                    <Globe className="size-3 shrink-0" />
                    {t('conversations.translation.replyHint', {
                  from: languageName(locale, locale),
                  to: translateReplies.targetLanguageName
                })}
                  </span> : null}

                <ToggleAIConversationDialog conversationId={conversationId} isAIEnabled={isAgentActive} title={t('conversations.sendMessage')} description={t('conversations.sendMessageWarning')} onConfirm={handleSend} trigger={<Button ref={dialogTriggerRef} size="sm" variant="secondary" className="disabled:cursor-not-allowed" disabled={isSendButtonDisabled}>
                      {isTranslatingReply ? <>
                          <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                          {t('conversations.translation.translating')}
                        </> : translateReplies && !preview ? t('conversations.translation.translateAndSend') : t('conversations.send')}
                    </Button>} shouldOpenDialog={sendingDisablesAgent && !preview} />
              </div>

              <input type="file" ref={fileInputRef} className="hidden" onChange={e_3 => handleFileUpload(e_3, 'file')} multiple />

              <input type="file" ref={imageInputRef} className="hidden" accept="image/*" onChange={e_4 => handleFileUpload(e_4, 'image')} multiple />
            </div>
          </TooltipTrigger>

          {(sendingDisablesAgent || isChatInputDisabled) && <TooltipContent className="bg-slate-600 text-primary-foreground duration-2">
              <p>
                {isChatInputDisabled ? t('conversations.cannotSendAfter24h') : t('conversations.sendingDisablesAgent')}
              </p>
            </TooltipContent>}
        </Tooltip>
      </TooltipProvider>
    </div>;
};
export { ChatInput };